import {durationBetween} from "../../../utils/DateTimeUtills";

export function floatToDurationString(floatValue, isDuration = true) {
    const isNegative = floatValue < 0; // Check if the input is negative
    const absoluteValue = Math.abs(floatValue); // Work with the absolute value

    const totalMinutes = Math.round(absoluteValue * 60); // Convert fractional hours to minutes
    const hours = Math.floor(totalMinutes / 60); // Extract hours
    const minutes = totalMinutes % 60; // Extract remaining minutes

    // Apply different formatting based on isDuration
    const formattedHours = isDuration ? String(hours) : String(hours).padStart(2, "0");
    const formattedMinutes = String(minutes).padStart(2, "0");

    // Add negative sign if the input was negative
    return `${isNegative ? "- " : ""}${formattedHours}:${formattedMinutes}`;
}

export const splitEventsPerDay = (events) => {
    const splitEvents = events.reduce((result, event) => {
        const date = event.eventTime.split('T')[0];
        if (!result[date]) {
            result[date] = [];
        }
        result[date].push(event);
        return result;
    }, {});

    return splitEvents;
}

export const calculateTotalHoursPerCategory = (events, category) => {
    if(!events || events.length < 1 || !category)
        return 0;

    const eventsOfCategory = events.filter(event => event.categoryType.toLowerCase() === category?.toLowerCase());
    const correlatedEvents = [];

    let total = 0;
    eventsOfCategory.forEach(event => {
        const correlationId = event.correlationId;
        if(correlatedEvents.some(correlationId => correlationId === event.correlationId))
            return;

        const correlatedEvent = events.find(other => other.correlationId === correlationId && other.uuid !== event.uuid);
        if(correlatedEvent)
        {
            total += durationBetween(event.eventTime, correlatedEvent.eventTime);
            correlatedEvents.push(correlationId);
        }
    });

    return total;
}



export const getEventByCode = (events, code, findEarliest = true) => {
    if(!events || !code)
        return null;

    const filteredEvents = events.filter(event => event.categoryCode?.toUpperCase() === code.toUpperCase());

    if (filteredEvents.length === 0) {
        return null;
    }

    return filteredEvents.reduce((result, current) => {
        const isBetter = findEarliest
            ? new Date(current.eventTime) < new Date(result.eventTime)
            : new Date(current.eventTime) > new Date(result.eventTime);
        return isBetter ? current : result;
    });
};

export const getWorkdayEnd = (endWorkdayEvent, day) => {
    if (endWorkdayEvent) {
        return endWorkdayEvent.eventTime;
    }

    const isToday = new Date().toLocaleDateString("sv") === day;
    if (isToday)
    {
        return null;
    }

    const midnight = new Date(day);
    midnight.setHours(23, 59, 59, 999);

    return midnight.toISOString();
}

export const getWorkdayDuration = (day, beginWorkdayEvent, endWorkdayEvent) => {
    if(beginWorkdayEvent && endWorkdayEvent)
    {
        return durationBetween(beginWorkdayEvent.eventTime, endWorkdayEvent.eventTime);
    }

    if (!beginWorkdayEvent)
        return 0;

    const isToday = formatDay(day) === formatDay(new Date());
    return isToday ? durationBetween(beginWorkdayEvent.eventTime, new Date()) : durationBetween(beginWorkdayEvent.eventTime, getWorkdayEnd(endWorkdayEvent, formatDay(day)));
}

export const getBriefOverviewForDay = (workday, events) => {
    const day = workday.date.day ?? workday.date.toLocaleDateString("sv") // Svenska uses the format "yyyy-MM-dd"
    const eventsForToday = splitEventsPerDay(events)[day];

    const beginWorkday = getEventByCode(eventsForToday, 'begin_workday', true);
    const endWorkday = getEventByCode(eventsForToday, 'end_workday', false);

    const workdayStart = beginWorkday?.eventTime || workday.firstInTime;
    const workdayEnd = getWorkdayEnd(endWorkday, day);

    // const workdayDuration = workdayEnd ? durationBetween(workdayStart, workdayEnd) : 0
    const workdayDuration = getWorkdayDuration(new Date(day), beginWorkday, endWorkday);

    const totalHoursBreak =  calculateTotalHoursPerCategory(eventsForToday, "break");
    const totalHoursPrivate =  calculateTotalHoursPerCategory(eventsForToday, "private");
    const totalHoursTravel = calculateTotalHoursPerCategory(eventsForToday, "travel");
    const totalHoursGeofenceEvents = calculateTotalHoursPerCategory(eventsForToday, "workLocation");
    const totalHoursCustomEvents = totalHoursGeofenceEvents + calculateTotalHoursPerCategory(eventsForToday, "custom");
    const totalUnallocatedHours = workdayDuration - workday.totalWorkLocations - totalHoursBreak - totalHoursPrivate - totalHoursTravel - totalHoursCustomEvents;
    const totalAllocatedHours = workday.totalWorkLocations + totalHoursBreak + totalHoursPrivate + totalHoursTravel + totalHoursCustomEvents;

    return {
        workdayStart,
        workdayEnd,
        workdayDuration,
        breakDuration: totalHoursBreak,
        privateDuration: totalHoursPrivate,
        travelDuration: totalHoursTravel,
        customEventsDuration: totalHoursCustomEvents,
        unallocatedDuration: totalUnallocatedHours,
        allocatedDuration: totalAllocatedHours,
        eventsDuringDay: eventsForToday
    }
}

export const groupTransactionsPerDay = (transactions) => {
    return transactions.reduce((acc, curr) => {
        // Extract the day from startTime (e.g., "2024-11-22")
        const day = curr.startTime.split('T')[0];

        // If the day is not yet in the accumulator, initialize it
        if (!acc[day]) {
            acc[day] = {
                day: day,
                totalDuration: 0,
                transactions: [],
                geofences: [],
                geofenceNames: new Set(), // Using Set to avoid duplicates
                firstInTime: curr.startTime,
                lastOutTime: curr.endTime,
            };
        }

        // Add the duration for the current entry
        acc[day].totalDuration += curr.durationHours;

        // Add the geofence name to the set
        acc[day].geofenceNames.add(curr.geofenceName);

        // Update the earliest start time
        if (new Date(curr.startTime) < new Date(acc[day].firstInTime)) {
            acc[day].firstInTime = curr.startTime;
        }

        // Update the latest end time
        if (new Date(curr.endTime) > new Date(acc[day].lastOutTime)) {
            acc[day].lastOutTime = curr.endTime;
        }

        acc[day].transactions.push(curr);
        let geofenceRecord = acc[day].geofences.find(g => g.id === curr.geofenceId);

        if(!geofenceRecord)
        {
            const geofence = {
                id: curr.geofenceId,
                name: curr.geofenceName,
                notes: [],
                transactions: [],
                duration: 0
            }

            if(curr.note)
                geofence.notes.push(curr.note);

            acc[day].geofences.push(geofence);
            geofenceRecord = geofence;
        }
        else if(curr.note)
        {
            acc[day].geofences.find(g => g.id === curr.geofenceId).notes.push(curr.note);
        }

        geofenceRecord.duration += curr.durationHours;
        geofenceRecord.transactions.push(curr);

        return acc;
    }, {});
}

export const findCorrelatingEvent = (event, timelineEvents) => {
    // Find event with the same correlation ID but event time greater than the current event.
    const correlatingEvents = timelineEvents.filter(
        e => e.correlationId === event.correlationId && e.uuid !== event.uuid
    );

    const lastEvent = correlatingEvents.find(e => new Date(e.eventTime) > new Date(event.eventTime));

    if (lastEvent) return lastEvent;

    return correlatingEvents.length > 1 ? correlatingEvents[correlatingEvents.length - 1] : null;
};

export const correlateEvents = (events) => {
    if(!events)
        return [];

    let correlatedEvents = [];
    let output = [];

    events.forEach(event => {
        // Skip events that were already correlated (merged)
        if (correlatedEvents.includes(event.correlationId))
            return;

        const correlatingEvent = findCorrelatingEvent(event, events);
        correlatingEvent && correlatedEvents.push(correlatingEvent.correlationId);

        output.push({
            startTime: event.eventTime,
            ...event,
            ...(correlatingEvent && { endTime: correlatingEvent.eventTime, duration: durationBetween(event.eventTime, correlatingEvent.eventTime) })
        });
    });

    return output;
}

export const formatDay = (day) => {
    return `${day.getFullYear()}-${(day.getMonth() + 1).toString().padStart(2, '0')}-${day.getDate().toString().padStart(2, '0')}`
}